import PropTypes from 'prop-types';
import moment from 'moment';
import {useDispatch, useSelector} from 'react-redux';
import HeatmapCalendar, {HEAT_VALUE_COLOR} from '../../../../components/calendar/heatmapCalendar';
import {
    changeFilterValue,
    changeSelectedDate, filterClinicNamesState, filterSessionNamesState,
    filterStats,
    monthStatsState,
    selectedDate as sliceSelectedDate, selectedFilters,
    updateMonthStatCached,
} from '../../../../store/reducers/appointments';
import React, {useCallback, useEffect, useState, forwardRef} from 'react';
import makeStyles from '@mui/styles/makeStyles';
import {Stack} from '@mui/material';
import {find, isEmpty, isNil, isNull} from 'lodash';
import AgyleMultiselectAutocomplete from '../../../../components/ui/selector/agyleMultiselectAutocomplete';
import {useTranslation} from 'react-i18next';

const useStyles = makeStyles((theme) => ({
    highAvailability: {
        backgroundColor: HEAT_VALUE_COLOR.HIGH_AVAILABILITY,

        '&.MuiPickersDay-root': {
            backgroundColor: HEAT_VALUE_COLOR.HIGH_AVAILABILITY,

            '&.Mui-selected': {
                backgroundColor: HEAT_VALUE_COLOR.HIGH_AVAILABILITY,
            },
        },
    },
    mediumAvailability: {
        backgroundColor: HEAT_VALUE_COLOR.MEDIUM_AVAILABILITY,

        '&.MuiPickersDay-root': {
            backgroundColor: HEAT_VALUE_COLOR.MEDIUM_AVAILABILITY,

            '&.Mui-selected': {
                backgroundColor: HEAT_VALUE_COLOR.MEDIUM_AVAILABILITY,
            },
        },
    },
    lowAvailability: {
        backgroundColor: HEAT_VALUE_COLOR.LOW_AVAILABILITY,

        '&.MuiPickersDay-root': {
            backgroundColor: HEAT_VALUE_COLOR.LOW_AVAILABILITY,

            '&.Mui-selected': {
                backgroundColor: HEAT_VALUE_COLOR.LOW_AVAILABILITY,
            },
        },
    },
    dictionaryTextField: {
        backgroundColor: theme.palette.background.paper,
    },
}));

const AppointmentsHeatmapCalendar = forwardRef(({ selectedClinic, selectedSession, hideButton, button }, ref) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const filters = useSelector(selectedFilters);
    const selectedDate = useSelector(sliceSelectedDate);
    const monthStatCalendarFull = useSelector(monthStatsState);
    const selectedFiltersSelector = useSelector(selectedFilters);
    const filterClinicNames = useSelector(filterClinicNamesState);
    const filterSessionNames = useSelector(filterSessionNamesState);

    const [calendarDate, setCalendarDate] = useState(selectedDate || moment());

    const [sessionFilter, setSessionFilter] = useState(null);
    const [clinicNameFilter, setClinicNameFilter] = useState(null);
    const [uniqueSessions, setUniqueSessions] = useState([]);
    const [clinicsInSessionList, setClinicsInSessionList] = useState([]);

    // const [clinicsLoading, setClinicsLoading] = useState(null);
    const [isLoading, setLoading] = useState(false);
    const [monthStatCalendar, setMonthStatCalendar] = useState({});

    useEffect(() => {
        const msc =  monthStatCalendarFull[moment(calendarDate)?.format('YYYY_MM')] || {};

        setUniqueSessions(msc.sessionDicts?.uniqueSessions || []);
        setClinicsInSessionList(msc.sessionDicts?.clinics || []);

        const filterss = {
            startDate: moment(calendarDate).startOf('month'),
            endDate: moment(calendarDate).endOf('month'),
            clinics: clinicNameFilter ? [clinicNameFilter] : filters.clinics || [],
            sessions: sessionFilter ? [sessionFilter] : filters.sessions || [],
        }

        let filtered =   filterStats(msc,filterss);

        setMonthStatCalendar(filtered);
    }, [monthStatCalendarFull, selectedDate,calendarDate,clinicNameFilter,sessionFilter, filters]);

    useEffect(() => {
        dispatch(updateMonthStatCached(calendarDate.clone().startOf('month')));
    }, [calendarDate]);

    const changeHeatmapDate = useCallback(
        (momentDate) => {
            dispatch(changeSelectedDate(moment(momentDate)));
        },
        [dispatch],
    );

    const getTileFeedbackBackground = useCallback(
        (dayElement) => {
            const day = moment(dayElement).date();

            if (monthStatCalendar?.[day]?.allslots === monthStatCalendar?.[day]?.takenslots) return null;
            if ((monthStatCalendar?.[day]?.takenslots / monthStatCalendar?.[day]?.allslots) > 0.75) return classes.mediumAvailability;

            return classes.highAvailability;
        },
        [monthStatCalendar],
    );

    const handleMonthChange = useCallback((date) => {
        if (moment(date).isValid()) {
            setCalendarDate(moment(date));
        }
    }, []);

    useEffect(() => {
        setCalendarDate(selectedDate);
    }, [selectedDate]);

    useEffect(() => {
        if (!isNull(selectedClinic) && !isEmpty(clinicsInSessionList)) {
            const found = find(clinicsInSessionList, { value: selectedClinic?.__id });
            if (found) setClinicNameFilter(selectedClinic);
        } else {
            setClinicNameFilter(null)
        }

        if (!isNull(selectedSession) && !isEmpty(uniqueSessions)) {
            const found = find(uniqueSessions, { value: selectedSession?.groupId });
            setSessionFilter(found);
        } else {
            setSessionFilter(null)
        }
    }, [selectedClinic?.__id, selectedSession?.groupId, uniqueSessions, clinicsInSessionList])


    const handleChangeFilter = (key, value) => {
        dispatch(changeFilterValue(value,key));
    };

    return (
        <HeatmapCalendar
            ref={ref}
            loading={isLoading}
            hideButton={hideButton}
            button={button}
            onDateChange={changeHeatmapDate}
            onMonthChange={handleMonthChange}
            dayStyleCallback={getTileFeedbackBackground}
            initialDate={moment(selectedDate, 'DD/MM/YYYY').toISOString()}
        >
            {/* For fix Failed prop type `children` */}
            <>
                {(!selectedClinic || !selectedSession) && !hideButton && (
                    <Stack gap={1}>
                        {isNil(selectedClinic) && (
                            <AgyleMultiselectAutocomplete
                                options={filterClinicNames}
                                onChange={(val) => handleChangeFilter('clinics', val)}
                                label={t('Clinic Name')}
                                withNewBackground
                                value={selectedFiltersSelector?.clinics}
                                size="medium"
                                pathToItemValue='__id'
                                fullObjecValue={true}
                                noBorder={false}
                                style={{width: '100%'}}
                                showSelectAll={false}
                                selectNoneLabel={"Clear"}
                                autocomplete={true}
                            />
                        )}

                        {isNil(selectedSession) && (
                            <AgyleMultiselectAutocomplete
                                options={filterSessionNames}
                                onChange={(val) => handleChangeFilter('sessions', val)}
                                label={t('Session Name')}
                                value={selectedFiltersSelector?.sessions}
                                size="medium"
                                inputProps={{
                                    style: {color: 'white'}
                                }}
                                withNewBackground
                                pathToItemValue='__id'
                                fullObjecValue={true}
                                noBorder={false}
                                style={{width: '100%'}}
                                showSelectAll={false}
                                selectNoneLabel={"Clear"}
                                autocomplete={true}
                            />
                        )}
                    </Stack>
                )}
            </>
        </HeatmapCalendar>
    );
})

AppointmentsHeatmapCalendar.propTypes = {
    hideButton: PropTypes.bool,
    selectedDay: PropTypes.string,
};

AppointmentsHeatmapCalendar.defaultProps = {
    hideButton: false,
    selectedDay: moment().toISOString(),
};

export default AppointmentsHeatmapCalendar;